/**
 * 🛠️ Theme Utilities
 * Helper functions for working with the centralized theme system
 */

import { components, colors, logo } from '../styles/theme.js';

/**
 * Build className strings with theme-aware utilities
 */
export const cn = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

/**
 * Get theme-aware button classes
 */
export const getButtonClasses = (variant = 'primary', size = 'md', disabled = false) => {
  if (disabled) {
    return cn(components.button.base, components.button.disabled);
  }

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base', 
    lg: 'px-6 py-3 text-lg',
  };

  const variantClasses = {
    primary: components.button.primary,
    secondary: components.button.secondary,
    danger: components.button.danger,
    ghost: components.button.ghost,
  };

  return cn(
    components.button.base,
    sizeClasses[size],
    variantClasses[variant]
  );
};

/**
 * Get theme-aware input classes
 */
export const getInputClasses = (error = false) => {
  return cn(
    components.input.base,
    error ? components.input.error : components.input.focus
  );
};

/**
 * Get theme-aware card classes
 */
export const getCardClasses = (hover = false, shadow = true) => {
  return cn(
    components.card.base,
    hover ? components.card.hover : '',
    shadow ? components.card.shadow : '',
    hover && shadow ? components.card.shadowHover : ''
  );
};

/**
 * Get theme-aware text classes
 */
export const getTextClasses = (variant = 'primary', size = 'base', weight = 'normal') => {
  const variantClasses = components.text[variant] || components.text.primary;
  
  const sizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base', 
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl',
  };

  const weightClasses = {
    normal: 'font-normal',
    medium: 'font-medium', 
    semibold: 'font-semibold',
    bold: 'font-bold',
  };

  return cn(variantClasses, sizeClasses[size], weightClasses[weight]);
};

/**
 * Get theme-aware status/badge classes
 */
export const getStatusClasses = (variant = 'info', size = 'sm') => {
  const variantClasses = components.status[variant] || components.status.info;
  
  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5',
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-2.5 py-1.5',
  };

  return cn(variantClasses, sizeClasses[size], 'rounded-full font-medium');
};

/**
 * Generate CSS custom properties from theme colors
 */
export const getCSSVariables = () => {
  return {
    '--color-primary': colors.primary,
    '--color-secondary': colors.secondary,
    '--color-tertiary': colors.tertiary,
    '--color-accent': colors.accent,
    '--color-accent-hover': colors.accentHover,
    '--color-accent-light': colors.accentLight,
    '--color-text-primary': colors.textPrimary,
    '--color-text-secondary': colors.textSecondary,
    '--color-border': colors.border,
    '--color-gold': colors.gold,
  };
};

/**
 * Apply theme to document root
 */
export const applyThemeToRoot = () => {
  const root = document.documentElement;
  const variables = getCSSVariables();
  
  Object.entries(variables).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
};

/**
 * Common layout classes
 */
export const layout = {
  container: 'max-w-6xl mx-auto px-4 sm:px-6 lg:px-8',
  section: 'py-12',
  card: getCardClasses(true, true),
  formGroup: 'space-y-1',
};

/**
 * Common component presets
 */
export const presets = {
  // Navigation
  navActive: components.nav.active,
  navInactive: components.nav.inactive,
  
  // Buttons
  primaryButton: getButtonClasses('primary', 'md'),
  secondaryButton: getButtonClasses('secondary', 'md'),
  dangerButton: getButtonClasses('danger', 'md'),
  
  // Text
  heading: getTextClasses('primary', '2xl', 'bold'),
  subheading: getTextClasses('primary', 'lg', 'semibold'),
  body: getTextClasses('secondary', 'base', 'normal'),
  muted: getTextClasses('muted', 'sm', 'normal'),
  
  // Form elements
  input: getInputClasses(),
  inputError: getInputClasses(true),
  
  // Status
  successBadge: getStatusClasses('success'),
  warningBadge: getStatusClasses('warning'),
  errorBadge: getStatusClasses('error'),
  infoBadge: getStatusClasses('info'),
};

/**
 * Get theme-aware logo classes and paths
 */
export const getLogoPath = (variant = 'main') => {
  return logo.paths[variant] || logo.paths.main;
};

export const getLogoSize = (size = 'md') => {
  return logo.sizes[size] || logo.sizes.md;
};

export const getLogoClasses = (size = 'md', className = '') => {
  return cn(getLogoSize(size), className);
};

export default {
  cn,
  getButtonClasses,
  getInputClasses, 
  getCardClasses,
  getTextClasses,
  getStatusClasses,
  getLogoPath,
  getLogoSize,
  getLogoClasses,
  getCSSVariables,
  applyThemeToRoot,
  layout,
  presets,
};